import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import TexHubLogo from '@/components/brand/TexHubLogo';
import RegionSelector from '@/components/RegionSelector';
import { 
  Store, 
  Search, 
  MapPin, 
  Star, 
  ArrowRight,
  Crown,
  CheckCircle2,
  Sparkles,
  Loader2,
  ShoppingBag,
  Package,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const StoresDirectory = () => {
  const { isUSA } = useLocale();
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: isUSA ? 'All' : 'Todas' },
    { id: 'telas', label: isUSA ? 'Fabrics' : 'Telas' },
    { id: 'ropa', label: isUSA ? 'Clothing' : 'Ropa' },
    { id: 'accesorios', label: isUSA ? 'Accessories' : 'Accesorios' },
    { id: 'hilos', label: isUSA ? 'Threads' : 'Hilos' },
  ];

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stores/directory`);
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStores = stores.filter(store => {
    const matchesSearch = store.store_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         store.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || store.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const isBuyer = user?.account_type === 'buyer';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to={user ? '/dashboard' : '/'}>
              <TexHubLogo size="default" />
            </Link>
            <div className="flex items-center gap-3">
              <RegionSelector variant="minimal" showLabel={false} />
              {user ? (
                <Link to="/dashboard">
                  <Button variant="outline" size="sm">
                    {isUSA ? 'Dashboard' : 'Mi Panel'}
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" size="sm">
                      {isUSA ? 'Log in' : 'Iniciar sesión'}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                      {isUSA ? 'Create account' : 'Crear cuenta'}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
            {isUSA ? 'Explore Online Stores' : 'Explora Tiendas en Línea'}
          </h1>
          <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
            {isUSA 
              ? 'Find verified suppliers and manufacturers. Order directly from their online stores.'
              : 'Encuentra proveedores y fabricantes verificados. Pide directamente en sus tiendas en línea.'
            }
          </p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder={isUSA ? 'Search stores or products...' : 'Buscar tiendas o productos...'}
              className="h-12 pl-12 pr-4 bg-white text-slate-900 border-0 shadow-lg"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="store-search-input"
            />
          </div>
        </div>
      </div>

      {/* Seller Promo Banner */}
      {(!user || isBuyer) && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-4">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3 text-white">
                <Crown className="w-6 h-6" />
                <span className="font-medium">
                  {isUSA 
                    ? '¿Want your own online store? Start selling today!'
                    : '¿Quieres tu propia tienda en línea? ¡Empieza a vender hoy!'
                  }
                </span>
              </div>
              <Link to={user ? '/pricing' : '/register'}>
                <Button className="bg-white text-orange-600 hover:bg-orange-50">
                  <Sparkles className="w-4 h-4 mr-2" />
                  {isUSA ? 'Create my store' : 'Crear mi tienda'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Categories */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {categories.map(cat => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
              className={selectedCategory === cat.id ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        )}

        {/* Stores Grid */}
        {!loading && filteredStores.length === 0 ? (
          <div className="text-center py-12">
            <Store className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="font-semibold text-slate-700 mb-2">
              {isUSA ? 'No stores found' : 'No se encontraron tiendas'}
            </h3>
            <p className="text-slate-500">
              {isUSA ? 'Try a different search or category' : 'Intenta con otra búsqueda o categoría'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStores.map((store) => (
              <Link key={store.store_id} to={`/tienda/${store.slug}`}>
                <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full" data-testid={`store-card-${store.slug}`}>
                  {/* Banner */}
                  <div className="h-32 bg-gradient-to-br from-emerald-400 to-teal-500 relative">
                    {store.banner_url && (
                      <img 
                        src={store.banner_url} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    )}
                    {/* Logo */}
                    <div className="absolute -bottom-8 left-4">
                      <div className="w-16 h-16 rounded-xl bg-white shadow-lg overflow-hidden border-2 border-white">
                        {store.logo_url ? (
                          <img src={store.logo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-emerald-100 flex items-center justify-center">
                            <Store className="w-8 h-8 text-emerald-600" />
                          </div>
                        )}
                      </div>
                    </div>
                    {store.is_featured && (
                      <Badge className="absolute top-2 right-2 bg-amber-500">
                        <Star className="w-3 h-3 mr-1" />
                        {isUSA ? 'Featured' : 'Destacada'}
                      </Badge>
                    )}
                  </div>
                  
                  <CardContent className="pt-10 pb-4">
                    <h3 className="font-semibold text-slate-900 mb-1">{store.store_name}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                      {store.description || (isUSA ? 'Online store' : 'Tienda en línea')}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Package className="w-4 h-4" />
                        <span>{store.product_count || 0} {isUSA ? 'products' : 'productos'}</span>
                      </div>
                      <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                        {isUSA ? 'Visit' : 'Visitar'}
                        <ExternalLink className="w-4 h-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Bottom CTA for non-logged users */}
        {!user && (
          <Card className="mt-12 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
            <CardContent className="p-8 text-center">
              <Crown className="w-12 h-12 mx-auto mb-4 text-amber-400" />
              <h2 className="text-2xl font-bold mb-2">
                {isUSA ? 'Start selling online today' : 'Empieza a vender en línea hoy'}
              </h2>
              <p className="text-slate-300 mb-6 max-w-lg mx-auto">
                {isUSA 
                  ? 'Create your free account and launch your online store in minutes. Reach thousands of buyers in the textile industry.'
                  : 'Crea tu cuenta gratis y lanza tu tienda en línea en minutos. Llega a miles de compradores en la industria textil.'
                }
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link to="/register">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                    <Sparkles className="w-5 h-5 mr-2" />
                    {isUSA ? 'Create free account' : 'Crear cuenta gratis'}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    {isUSA ? 'Log in' : 'Iniciar sesión'}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default StoresDirectory;
